import { useState } from "react";
import { Info } from "lucide-react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import type { TradingViewAiOverlaysToggleProps } from "@/types/tradingTool";
import { cn } from "@/utils/utils";
import { tradingViewAiOverlaysAtom } from "@/store/atoms";

export default function TradingViewAiOverlaysToggle({
  className,
}: TradingViewAiOverlaysToggleProps) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useAtom(tradingViewAiOverlaysAtom);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className={cn("relative", className)}>
      <div className="btn-gradient-left rounded-md ">
        <div className="bg-[#0b0b0b]/70 flex items-center gap-2.5 rounded-md px-3 py-[9px]">
          <span className="whitespace-nowrap font-neue text-sm font-medium text-white">
            {t("AI Overlays")}
          </span>

          <button
            type="button"
            aria-label={t("AI Overlays info")}
            className="text-white/70 transition-colors hover:text-white"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
          >
            <Info className="h-4 w-4" />
          </button>

          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label={t("Toggle AI Overlays")}
            onClick={() => setEnabled((current) => !current)}
            className={cn(
              "relative h-5 w-9 shrink-0 rounded-xl transition-colors",
              enabled ? "bg-[#9ED473]" : "bg-white/20",
            )}
          >
            <span
              className={cn(
                "absolute top-0.5 h-4 w-4 rounded-xl bg-white shadow transition-transform",
                enabled ? "translate-x-[0px]" : "-translate-x-4",
              )}
            />
          </button>
        </div>
      </div>

      {showTooltip && (
        <div
          role="tooltip"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-[280px] rounded-lg border border-white/10 bg-[#121212] px-3 py-2.5 font-neue text-xs leading-relaxed text-white/70 shadow-xl"
        >
          {t(
            "Toggle AI-powered visual insights like trendlines, support/resistance zones, and key levels directly on your charts.",
          )}
        </div>
      )}
    </div>
  );
}
